import React, { useCallback, useState } from "react";
import { Select } from "antd";
import { Visit } from "../../interfaces/Visit";
import { updateField } from "../../utils/StateUtil";
import { useAppDispatch } from "../../hooks";
import { getMedicines } from "../../apis/medicine";
import { debounce } from "lodash";
import { Medicine } from "../../interfaces/Medicine";

type MedicineSelectProps = {
  visit: Visit;
  onMedicineCreate: (value: string) => void;
};

const MedicineSelect: React.FC<MedicineSelectProps> = ({ visit, onMedicineCreate }) => {
  const [medicines, setMedicines] = useState<Array<Medicine & { company?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const fetchMedicines = async (search: string = "") => {
    setLoading(true);
    try {
      const restrictToPharmacy = window.localStorage.getItem("restrictPrescriptionsToPharmacy") === "yes";
      const params: any = { autocomplete: true };
      if (search) params.search = search;
      if (restrictToPharmacy) params.restrict_to_pharmacy = true;
      const { data: { data: response } } = await getMedicines(params);
      setMedicines(response);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(debounce(fetchMedicines, 400), []);

  const handleSearch = (value: string) => {
    if (value.length >= 2) debouncedFetch(value);
  };

  const handleSelect = (_: any, option: any) => {
    const medicine = medicines.find((m) => m.id.toString() === option.value);
    if (!medicine) return;
    if (visit.medicines.some((m) => m?.id?.toString() === option.value)) return;

    updateField(
      "medicines",
      [
        ...visit.medicines,
        {
          id: option.value,
          name: medicine.name,
          instructions: {
            name: medicine.name,
            molecule: medicine.molecule ?? "",
            form: medicine.form ?? "tablet",
            morning: 0,
            afternoon: 0,
            evening: 0,
            night: 0,
            meal: "before_meal",
            note: "",
            quantity: 0,
            frequency: "daily",
            duration: "",
            power: "",
            unit: "",
            dosage: "",
            durationPeriod: "days",
          },
        },
      ],
      dispatch
    );
  };

  const handleDeselect = (value: string) => {
    const updated = visit.medicines.filter((m) => m?.id?.toString() !== value);
    updateField("medicines", updated, dispatch);
  };

  const handleCreate = (inputValue: string) => {
    onMedicineCreate(inputValue);
  };

  const selectedValues = visit.medicines.map((m: any) => ({
    value: m?.id?.toString(),
    label: m?.name,
  }));

  const options = medicines.map((m) => ({
    value: m.id.toString(),
    label: m.name,
    molecule: m.molecule,
    company: (m as any).company,
  }));

  return (
    <div className="block">
      <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
        Medicines
      </label>
      <Select
        mode="multiple"
        style={{ width: "100%" }}
        placeholder="Search by brand name, molecule or company..."
        showSearch
        filterOption={false}
        loading={loading}
        value={selectedValues}
        labelInValue
        onSearch={handleSearch}
        onSelect={handleSelect}
        onDeselect={(v: any) => handleDeselect(v.value)}
        notFoundContent={
          <div className="text-sm text-gray-400 px-2 py-1">
            Type to search medicines
          </div>
        }
        dropdownRender={(menu) => (
          <>
            {menu}
            <div
              className="px-3 py-2 text-sm text-indigo-600 cursor-pointer hover:bg-gray-50 border-t"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const input = document.querySelector(".ant-select-selection-search-input") as HTMLInputElement;
                if (input?.value) handleCreate(input.value);
              }}
            >
              + Add new medicine
            </div>
          </>
        )}
        optionRender={(option: any) => (
          <div className="flex flex-col py-0.5">
            <span className="font-medium text-gray-900">{option.data.label}</span>
            {(option.data.molecule || option.data.company) && (
              <span className="text-xs text-gray-400">
                {[option.data.molecule, option.data.company].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
        )}
        options={options}
      />
    </div>
  );
};

export default MedicineSelect;
